import
  React, { useState } from 'react';
import { Button, Modal, List, Spin, message, Checkbox, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import logger from '../../utils/logger'; // Import logger utility for controlled debugging
import { useAuth } from './security/AuthContext';
import { dccDownloadUrl, dccList } from '../../services/dccApi';
import FilePreviewModal, { canPreviewFile } from './FilePreviewModal';

interface FileDownloadToolProps {
  buttonText?: string | React.ReactNode;
  contentPath?: string;
}
const FileDownloadTool: React.FC<FileDownloadToolProps> = ({ buttonText = 'Download File', contentPath }) => {
  const { token } = useAuth();
  // Get the current path and content path
  const currentFolderPath = contentPath || '';
  
  // Build the full path for API
  const normalizedPath = currentFolderPath;

  const [visible, setVisible] = useState(false);
  const [fileList, setFileList] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewPath, setPreviewPath] = useState<string | null>(null);

  const fetchFileList = async () => {
    setLoading(true);
    try {
      if (!token) throw new Error('Not authenticated');

      logger.debug(`Attempting to fetch file list from path: ${normalizedPath}`);
      const data = await dccList(normalizedPath, token);
      if (Array.isArray(data)) {
        // Sadece dosyaları filtrele (klasörleri hariç tut)
        const files = data.filter(item => !item.endsWith('/'));
        setFileList(files);
      } else {
        setFileList([]);
      }
    } catch (err: any) {
      message.error('File List not received');
      setFileList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setVisible(true);
    fetchFileList();
  };

  const handleCancel = () => {
    setVisible(false);
    setSelectedFiles([]);
  };

  const handlePreview = (file: string) => {
    const fullPath = normalizedPath ? `${normalizedPath}/${file}` : file;
    setPreviewPath(fullPath);
    setIsPreviewOpen(true);
  };

  const handleDownload = () => {
    if (selectedFiles.length === 0) return;
    
    selectedFiles.forEach(file => {
      const fullPath = normalizedPath ? `${normalizedPath}/${file}` : file;
      const url = dccDownloadUrl(fullPath, token, false);
      window.open(url, '_blank');
    });

    setVisible(false);
    setSelectedFiles([]);
  };

  return (
    <>
      {React.isValidElement(buttonText) ? 
        React.cloneElement(buttonText as React.ReactElement, { onClick: handleOpen })
        : <Button onClick={handleOpen}>{buttonText}</Button>
      }
      <Modal
        title="Download"
        open={visible}
        onCancel={handleCancel}
        width={400}
        footer={ <Button type="primary" onClick={handleDownload} disabled={selectedFiles.length === 0}> {selectedFiles.length > 0 ? `${selectedFiles.length} Download File` : 'Download'} </Button> }
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin tip="Loading..." />
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <Checkbox
                checked={selectedFiles.length === fileList.length}
                indeterminate={selectedFiles.length > 0 && selectedFiles.length < fileList.length}
                onChange={e => setSelectedFiles(e.target.checked ? fileList : [])}
              >
                {selectedFiles.length > 0 ? `${selectedFiles.length} File Selected` : 'Select All'}
              </Checkbox>
            </div>
            <List
              dataSource={fileList}
              renderItem={item => (
                <List.Item
                  style={{ 
                    cursor: 'pointer', 
                    background: selectedFiles.includes(item) ? '#e6f7ff' : undefined,
                    padding: '8px 12px',
                    borderRadius: '4px',
                    marginBottom: '4px',
                    overflow: 'hidden'
                  }}
                  onClick={() => {
                    setSelectedFiles(prev => 
                      prev.includes(item) 
                        ? prev.filter(f => f !== item)
                        : [...prev, item]
                    );
                  }}
                >
                  <Checkbox checked={selectedFiles.includes(item)} style={{ marginRight: 8 }} />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, gap: 8, minWidth: 0 }}>
                    <Tooltip title={item} placement="topLeft">
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item}
                      </span>
                    </Tooltip>
                    {canPreviewFile(item) ? (
                      <Button
                        size="small"
                        type="default"
                        icon={<EyeOutlined />}
                        style={{ flexShrink: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePreview(item);
                        }}
                      />
                    ) : null}
                  </div>
                </List.Item>
              )}
              locale={{ emptyText: 'No File Found in Folder' }}
            />
          </>
        )}
      </Modal>

      <FilePreviewModal
        open={isPreviewOpen}
        path={previewPath}
        token={token}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewPath(null);
        }}
        width={900}
      />
    </>
  );
};

export default FileDownloadTool;
